<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Icon from 'next/icon/Icon.vue';
import BookableResourcesAPI from 'dashboard/api/planning/bookableResources';
import { resourceDisplayIcon } from './resourceIcons';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const TABS = ['equipment', 'person', 'role'];
const activeTab = ref('equipment');

const records = ref([]);
const loading = ref(false);
const saving = ref(false);

const form = reactive({
  name: '',
  kind: 'equipment',
  capacity: 1,
  active: true,
});
const isPopoverOpen = ref(false);
const popoverRef = ref(null);
const popoverPosition = reactive({ top: 0, left: 0 });
const popoverTriggerRect = ref(null);

const showDeleteModal = ref(false);
const selectedResource = ref(null);

const kindLabel = kind => {
  const key = {
    equipment: 'SETTINGS.RESOURCES.KINDS.EQUIPMENT',
    person: 'SETTINGS.RESOURCES.KINDS.PERSON',
    role: 'SETTINGS.RESOURCES.KINDS.ROLE',
  }[kind];
  return key ? t(key) : kind;
};

const countByKind = computed(() =>
  records.value.reduce((acc, resource) => {
    const kind = resource.kind || 'equipment';
    acc[kind] = (acc[kind] || 0) + 1;
    return acc;
  }, {})
);

const filteredRecords = computed(() =>
  records.value.filter(resource => resource.kind === activeTab.value)
);

const tabsForTabBar = computed(() =>
  TABS.map(key => ({
    key,
    label: kindLabel(key),
    count: countByKind.value[key] || 0,
  }))
);

const activeTabIndex = computed(() =>
  Math.max(TABS.indexOf(activeTab.value), 0)
);

const addButtonLabel = computed(() => {
  const labels = {
    equipment: 'SETTINGS.RESOURCES.ADD_EQUIPMENT',
    person: 'SETTINGS.RESOURCES.ADD_PERSON',
    role: 'SETTINGS.RESOURCES.ADD_ROLE',
  };
  return t(labels[activeTab.value] || 'SETTINGS.RESOURCES.ADD');
});

const emptyMessage = computed(() => {
  const messages = {
    equipment: 'SETTINGS.RESOURCES.EMPTY_EQUIPMENT',
    person: 'SETTINGS.RESOURCES.EMPTY_PERSON',
    role: 'SETTINGS.RESOURCES.EMPTY_ROLE',
  };
  return t(messages[activeTab.value] || 'SETTINGS.RESOURCES.EMPTY');
});

const onTabChange = tab => {
  activeTab.value = tab.key;
  router.replace({ query: { ...route.query, tab: tab.key } });
};

watch(
  () => route.query.tab,
  tab => {
    if (TABS.includes(tab)) activeTab.value = tab;
  },
  { immediate: true }
);

const PANEL_WIDTH = 380;
const VIEWPORT_SPACING = 8;

const resetForm = () => {
  form.name = '';
  form.kind = activeTab.value;
  form.capacity = 1;
  form.active = true;
};

const setPopoverPositionFromTrigger = triggerEl => {
  if (!triggerEl) return;
  const rect = triggerEl.getBoundingClientRect();
  popoverTriggerRect.value = rect;
  popoverPosition.top = rect.bottom + VIEWPORT_SPACING;
  popoverPosition.left = Math.min(
    Math.max(VIEWPORT_SPACING, rect.right - PANEL_WIDTH),
    window.innerWidth - PANEL_WIDTH - VIEWPORT_SPACING
  );
};

const updatePopoverPosition = () => {
  if (!popoverTriggerRect.value || !popoverRef.value) return;
  const triggerRect = popoverTriggerRect.value;
  const panelRect = popoverRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_SPACING;
  const spaceAbove = triggerRect.top - VIEWPORT_SPACING;
  if (panelRect.height > spaceBelow && spaceAbove > spaceBelow) {
    popoverPosition.top = Math.max(
      VIEWPORT_SPACING,
      triggerRect.top - panelRect.height - VIEWPORT_SPACING
    );
    return;
  }
  popoverPosition.top = Math.min(
    triggerRect.bottom + VIEWPORT_SPACING,
    window.innerHeight - panelRect.height - VIEWPORT_SPACING
  );
};

const openCreateFlapout = async event => {
  resetForm();
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const dismissFlapout = () => {
  isPopoverOpen.value = false;
};

const cancelFlapout = () => {
  isPopoverOpen.value = false;
  resetForm();
};

const goToResource = resource => {
  router.push({
    name: 'settings_resources_show',
    params: { resourceId: resource.id },
  });
};

const fetchRecords = async () => {
  loading.value = true;
  try {
    const { data } = await BookableResourcesAPI.getAll();
    records.value = data.payload || data || [];
  } catch (e) {
    useAlert(e.message || t('SETTINGS.RESOURCES.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const saveResource = async () => {
  if (!form.name.trim()) {
    useAlert(t('SETTINGS.RESOURCES.NAME_REQUIRED'));
    return;
  }
  saving.value = true;
  try {
    const { data } = await BookableResourcesAPI.create({
      name: form.name.trim(),
      kind: form.kind || activeTab.value,
      capacity: Number(form.capacity) || 1,
      active: form.active,
    });
    cancelFlapout();
    if (data?.id) {
      router.push({
        name: 'settings_resources_show',
        params: { resourceId: data.id },
      });
      return;
    }
    await fetchRecords();
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.base ||
        e.message ||
        t('SETTINGS.RESOURCES.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const toggleActive = async resource => {
  try {
    await BookableResourcesAPI.update(resource.id, {
      active: !resource.active,
    });
    await fetchRecords();
  } catch (e) {
    useAlert(e.message || t('SETTINGS.RESOURCES.SAVE_ERROR'));
  }
};

const openDeleteModal = resource => {
  selectedResource.value = resource;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  selectedResource.value = null;
};

const confirmDelete = async () => {
  if (!selectedResource.value) return;
  try {
    await BookableResourcesAPI.delete(selectedResource.value.id);
    closeDeleteModal();
    await fetchRecords();
  } catch (e) {
    useAlert(e.message || t('SETTINGS.RESOURCES.DELETE_ERROR'));
  }
};

const handleFlapoutEscape = e => {
  if (isPopoverOpen.value && e.key === 'Escape') dismissFlapout();
};

onMounted(async () => {
  document.addEventListener('keydown', handleFlapoutEscape);
  await fetchRecords();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleFlapoutEscape);
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('SETTINGS.RESOURCES.LOADING')"
    :no-records-found="!loading && !filteredRecords.length"
    :no-records-message="emptyMessage"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('SETTINGS.RESOURCES.TITLE')"
        :description="t('SETTINGS.RESOURCES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            md
            :label="addButtonLabel"
            @click="openCreateFlapout"
          />
        </template>
      </BaseSettingsHeader>

      <div class="mt-4">
        <TabBar
          :tabs="tabsForTabBar"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChange"
        />
      </div>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak text-sm">
            <th
              class="py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('SETTINGS.RESOURCES.TABLE.NAME') }}
            </th>
            <th
              class="w-32 py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('SETTINGS.RESOURCES.TABLE.CAPACITY') }}
            </th>
            <th
              class="w-24 py-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ t('SETTINGS.RESOURCES.TABLE.ACTIVE') }}
            </th>
            <th
              class="w-32 py-4 ltr:text-right rtl:text-left font-semibold text-n-slate-11"
            >
              {{ t('SETTINGS.RESOURCES.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="resource in filteredRecords"
            :key="resource.id"
            class="cursor-pointer border-b border-n-weak hover:bg-n-alpha-1"
            @click="goToResource(resource)"
          >
            <td class="py-4 font-medium text-n-slate-12">
              <div class="flex items-center gap-3">
                <img
                  v-if="resource.thumbnail"
                  :src="resource.thumbnail"
                  :alt="resource.name"
                  class="size-8 rounded-full object-cover"
                />
                <span
                  v-else
                  class="flex size-8 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
                >
                  <Icon
                    :icon="resourceDisplayIcon(resource, activeTab)"
                    class="size-4"
                  />
                </span>
                {{ resource.name }}
              </div>
            </td>
            <td class="py-4">
              {{ resource.capacity }}
            </td>
            <td class="py-4" @click.stop>
              <Switch
                :model-value="resource.active"
                @change="toggleActive(resource)"
              />
            </td>
            <td class="py-4" @click.stop>
              <div class="flex items-center justify-end gap-2">
                <Button
                  v-tooltip.top="t('SETTINGS.RESOURCES.EDIT_TOOLTIP')"
                  icon="i-lucide-pen-line"
                  md
                  slate
                  faded
                  @click="goToResource(resource)"
                />
                <Button
                  v-tooltip.top="t('SETTINGS.RESOURCES.DELETE_TOOLTIP')"
                  icon="i-lucide-trash-2"
                  md
                  ruby
                  faded
                  @click="openDeleteModal(resource)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <div
    v-show="isPopoverOpen"
    ref="popoverRef"
    v-on-click-outside="() => isPopoverOpen && dismissFlapout()"
    class="fixed z-50 w-[23.75rem] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg p-4"
    :style="{
      top: `${popoverPosition.top}px`,
      left: `${popoverPosition.left}px`,
    }"
  >
    <div class="mb-4">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('SETTINGS.RESOURCES.FORM.CREATE_TITLE') }}
      </h3>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ kindLabel(form.kind) }}
      </p>
    </div>

    <div class="space-y-4">
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('SETTINGS.RESOURCES.NAME') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          :placeholder="t('SETTINGS.RESOURCES.FORM.NAME_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12"
        />
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">
          {{ t('SETTINGS.RESOURCES.CAPACITY') }}
        </label>
        <input
          v-model.number="form.capacity"
          type="number"
          min="1"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-n-slate-12"
        />
      </div>
      <div class="flex items-center gap-3 py-2">
        <Switch v-model="form.active" />
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('SETTINGS.RESOURCES.FORM.ACTIVE_LABEL') }}
        </span>
      </div>
    </div>

    <div class="mt-4 flex items-center gap-2">
      <Button
        :label="t('SETTINGS.RESOURCES.CANCEL')"
        slate
        outlined
        class="w-full"
        @click="cancelFlapout"
      />
      <Button
        :label="t('SETTINGS.RESOURCES.FORM.CREATE')"
        class="w-full"
        :is-loading="saving"
        @click="saveResource"
      />
    </div>
  </div>

  <div
    v-if="showDeleteModal"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
    @click.self="closeDeleteModal"
  >
    <div
      class="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-800"
    >
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ t('SETTINGS.RESOURCES.DELETE_MODAL.TITLE') }}
      </h3>
      <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
        {{ t('SETTINGS.RESOURCES.DELETE_MODAL.DESCRIPTION_PRE') }}
        <span class="font-semibold">{{ selectedResource?.name }}</span>
        {{ t('SETTINGS.RESOURCES.DELETE_MODAL.DESCRIPTION_POST') }}
      </p>
      <div class="mt-5 flex items-center gap-2">
        <Button
          :label="t('SETTINGS.RESOURCES.CANCEL')"
          slate
          outlined
          class="w-full"
          @click="closeDeleteModal"
        />
        <Button
          :label="t('SETTINGS.RESOURCES.DELETE_MODAL.CONFIRM')"
          ruby
          class="w-full"
          @click="confirmDelete"
        />
      </div>
    </div>
  </div>
</template>
